import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Dices, Lock, LockOpen } from 'lucide-react';
import clsx from 'clsx';
import { clamp } from '../../core/math';

export interface SliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Double-clicking the row resets to this. */
  defaultValue?: number;
  unit?: string;
  scale?: 'linear' | 'log';
  tooltip?: string;
  disabled?: boolean;
  /** Locked parameters are skipped by randomise and by batch mode. */
  locked?: boolean;
  onToggleLock?: () => void;
  onRandomize?: () => void;
  /** Decimal places in the number field; derived from `step` when omitted. */
  precision?: number;
  /** Extra control rendered in the header row, e.g. the exploration-range button. */
  headerExtra?: ReactNode;
  /** Marks the track to show that the exploration range has been narrowed. */
  bulkRange?: [number, number];
}

const decimalsFor = (step: number): number => {
  if (step >= 1) return 0;
  const s = String(step);
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : Math.min(4, s.length - dot - 1);
};

export function Slider({
  label,
  value,
  onChange,
  min,
  max,
  step = 0.01,
  defaultValue,
  unit,
  scale = 'linear',
  tooltip,
  disabled = false,
  locked = false,
  onToggleLock,
  onRandomize,
  precision,
  headerExtra,
  bulkRange,
}: SliderProps): JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const digits = precision ?? decimalsFor(step);

  // Log scale keeps parameters like "particle count 100 … 200000" usable across
  // their whole range instead of cramming five orders of magnitude into the last 5 %.
  const toNorm = useCallback(
    (v: number): number => {
      if (scale === 'log') {
        const lo = Math.log(Math.max(1e-6, min));
        const hi = Math.log(Math.max(1e-6, max));
        return clamp((Math.log(Math.max(1e-6, v)) - lo) / (hi - lo), 0, 1);
      }
      return clamp((v - min) / (max - min), 0, 1);
    },
    [min, max, scale],
  );

  const fromNorm = useCallback(
    (t: number): number => {
      if (scale === 'log') {
        const lo = Math.log(Math.max(1e-6, min));
        const hi = Math.log(Math.max(1e-6, max));
        return Math.exp(lo + (hi - lo) * clamp(t, 0, 1));
      }
      return min + (max - min) * clamp(t, 0, 1);
    },
    [min, max, scale],
  );

  const quantise = useCallback(
    (v: number): number => {
      const snapped = Math.round(v / step) * step;
      return clamp(Number(snapped.toFixed(6)), min, max);
    },
    [step, min, max],
  );

  const commit = useCallback((v: number) => onChange(quantise(v)), [onChange, quantise]);

  const drag = useRef<{ startX: number; startValue: number; width: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.classList.add('dragging');
    // Absolute jump on press, then relative movement from there — modifiers change
    // the sensitivity of the relative part, which is what fine-tuning needs.
    const jumped = fromNorm((e.clientX - rect.left) / rect.width);
    commit(jumped);
    drag.current = { startX: e.clientX, startValue: jumped, width: rect.width };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const d = drag.current;
    if (!d) return;
    const sensitivity = e.shiftKey ? 0.1 : e.altKey ? 10 : 1;
    const dt = ((e.clientX - d.startX) / d.width) * sensitivity;
    commit(fromNorm(toNorm(d.startValue) + dt));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!drag.current) return;
    drag.current = null;
    document.body.classList.remove('dragging');
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Wheel fine-tuning. Registered natively so it can be non-passive and preventDefault.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || disabled) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const mult = e.shiftKey ? 0.1 : e.altKey ? 10 : 1;
      const dir = e.deltaY > 0 ? -1 : 1;
      commit(value + dir * step * mult * (scale === 'log' ? Math.max(1, Math.abs(value) * 0.1) / step : 1));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [value, step, scale, commit, disabled]);

  const norm = toNorm(value);
  const display = editing ?? value.toFixed(digits);

  return (
    <div className={clsx('group/slider py-1', disabled && 'pointer-events-none opacity-40')}>
      <div className="mb-1 flex items-center gap-1.5">
        <span className="flex-1 truncate text-[11px] text-ink-300" title={tooltip ?? label}>
          {label}
        </span>

        {headerExtra}
        <button
          type="button"
          title="Nur diesen Wert neu würfeln"
          onClick={onRandomize}
          className={clsx(
            'ghost-button h-5 w-5 p-0 opacity-0 transition-opacity group-hover/slider:opacity-100',
            !onRandomize && 'hidden',
          )}
        >
          <Dices size={11} />
        </button>
        <button
          type="button"
          title={locked ? 'Entsperren — Randomize und Batch dürfen wieder ändern' : 'Sperren — vor Randomize und Batch schützen'}
          onClick={onToggleLock}
          className={clsx(
            'ghost-button h-5 w-5 p-0 transition-opacity',
            !onToggleLock && 'hidden',
            locked ? 'text-accent-500 opacity-100' : 'opacity-0 group-hover/slider:opacity-100',
          )}
        >
          {locked ? <Lock size={11} /> : <LockOpen size={11} />}
        </button>

        <input
          className="w-[62px] rounded border border-transparent bg-ink-850 px-1.5 py-0.5 text-right
                     font-mono text-[11px] text-ink-100 outline-none transition-colors
                     hover:border-ink-600 focus:border-ink-500"
          value={display}
          onChange={(e) => setEditing(e.target.value)}
          onFocus={(e) => {
            setEditing(value.toFixed(digits));
            e.target.select();
          }}
          onBlur={() => {
            if (editing !== null) {
              const parsed = Number.parseFloat(editing.replace(',', '.'));
              if (Number.isFinite(parsed)) commit(parsed);
            }
            setEditing(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setEditing(null);
              e.currentTarget.blur();
            }
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
              const mult = e.shiftKey ? 0.1 : e.altKey ? 10 : 1;
              commit(value + (e.key === 'ArrowUp' ? 1 : -1) * step * mult);
              setEditing(null);
            }
          }}
        />
        {unit && <span className="w-6 shrink-0 text-[10px] text-ink-500">{unit}</span>}
      </div>

      <div
        ref={trackRef}
        role="slider"
        tabIndex={-1}
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        title={tooltip}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={() => defaultValue !== undefined && commit(defaultValue)}
        className="relative h-[18px] cursor-ew-resize touch-none select-none"
      >
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-ink-750" />
        {/* The band randomise and batch may draw from, when it has been narrowed. */}
        {bulkRange && (
          <div
            className="pointer-events-none absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-accent-500/25"
            style={{
              left: `${toNorm(bulkRange[0]) * 100}%`,
              width: `${Math.max(0, toNorm(bulkRange[1]) - toNorm(bulkRange[0])) * 100}%`,
            }}
          />
        )}
        <div
          className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-ink-500 transition-colors group-hover/slider:bg-ink-400"
          style={{ width: `${norm * 100}%` }}
        />
        <div
          className="absolute top-1/2 h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full
                     border border-ink-900 bg-ink-200 shadow-sm transition-transform group-hover/slider:scale-110"
          style={{ left: `${norm * 100}%` }}
        />
        {defaultValue !== undefined && (
          <div
            className="pointer-events-none absolute top-1/2 h-[7px] w-px -translate-y-1/2 bg-ink-600"
            style={{ left: `${toNorm(defaultValue) * 100}%` }}
            title={`Standard: ${defaultValue}`}
          />
        )}
      </div>
    </div>
  );
}
